import { setAttributes, addListener, removeListener } from 'utils/dom';

export default class Tabs {
  constructor(container, options) {
    this.container = container;
    this.tabButton = options.tabButton;
    this.tabPanel = options.tabPanel;
    this.tabNav = options.tabNav;
    this.showFirst = options.showFirst;
    this.showRandom = options.showRandom;
    this.preventDefault = options.preventDefault;
    this.cb = options.cb;
    this.delay = options.delay || undefined;
    this.activePanel = null;
  }

  handleCallbacks = (target, targetId) => {
    let activePanel;
    this.tabPanel.forEach(panel => {
      const tabpanelAria = panel.getAttribute('aria-labelledby');
      if (targetId === tabpanelAria) activePanel = panel;
    });

    this.cb && this.cb(target, activePanel);
  };

  getRandomItem = () => {
    const randomItem = Math.floor(Math.random() * this.tabButton.length);
    this.initTabs(randomItem);
  };

  initTabs = index => {
    const activeBtn = this.tabButton[index];
    const activePanel = this.tabPanel[index];
    this.activePanel = this.tabPanel[index];

    // CSS should handle active styling!
    setAttributes(activeBtn, {
      'aria-selected': 'true',
      tabindex: '0',
    });

    // CSS should handle active styling!
    setAttributes(activePanel, {
      'aria-hidden': 'false',
    });
    this.handleCallbacks(activeBtn, activePanel.id);
  };

  toggleTabs = (target, event) => {
    this.preventDefault && event.preventDefault();
    const targetId = target.id;
    const oldActivePanel = this.activePanel;

    if (this.delay) {
      oldActivePanel.classList.add('fade-out');
      setTimeout(() => {
        oldActivePanel.classList.remove('fade-out');
      }, this.delay);

      this.resetTabs();
      this.setActiveTab(target, targetId);
    } else {
      this.resetTabs();
      this.setActiveTab(target, targetId);
    }
  };

  setActiveTab = (target, targetId) => {
    setAttributes(target, {
      'aria-selected': 'true',
      tabindex: '0',
    });

    this.tabPanel.forEach(panel => {
      const tabpanelAria = panel.getAttribute('aria-labelledby');

      if (targetId === tabpanelAria) {
        this.activePanel = panel;

        if (this.delay) {
          setTimeout(() => {
            panel.classList.add('is-active');
          }, this.delay);

          setTimeout(() => {
            panel.classList.add('is-active');
            panel.classList.remove('is-active');
            setAttributes(panel, {
              'aria-hidden': 'false',
            });
          }, this.delay * 2);
        } else {
          setAttributes(panel, {
            'aria-hidden': 'false',
          });
        }
      }
    });

    this.handleCallbacks(target, targetId);
  };

  resetTabs = () => {
    this.tabButton.forEach(button =>
      setAttributes(button, {
        'aria-selected': 'false',
        tabindex: '-1',
      })
    );

    if (this.delay) {
      setTimeout(() => {
        this.tabPanel.forEach(panel =>
          setAttributes(panel, {
            'aria-hidden': 'true',
          })
        );
      }, this.delay);
    } else {
      this.tabPanel.forEach(panel =>
        setAttributes(panel, {
          'aria-hidden': 'true',
        })
      );
    }
  };

  setAccessibility = event => {
    const { target, keyCode } = event;

    // arrowUp = 38
    // arrowRight 39
    // arrowDown = 40
    // arrowLeft = 37

    // Prevent arrow scrolling the page
    if ([37, 38, 39, 40].indexOf(event.keyCode) > -1) {
      event.preventDefault();
    }

    // arrow navigation between header elements
    if (keyCode === 39 || keyCode === 40) {
      // focus next tab-button
      const nextTab = target.parentElement.nextElementSibling
        ? target.parentElement.nextElementSibling.firstElementChild
        : 'undefined';
      const nextTabId = nextTab.id;

      // reset all tabs
      this.resetTabs();

      // if next tab exists, focus it. Else, focus the first tab in the list.
      if (nextTab !== 'undefined') {
        nextTab.focus();
        this.setActiveTab(nextTab, nextTabId);
      } else {
        const firstTab = this.tabNav.firstElementChild.firstElementChild;
        const firstTabId = firstTab.id;

        firstTab.focus();
        this.setActiveTab(firstTab, firstTabId);
      }
    } else if (keyCode === 37 || keyCode === 38) {
      // focus previous tab-button
      const prevTab = target.parentElement.previousElementSibling
        ? target.parentElement.previousElementSibling.firstElementChild
        : 'undefined';
      const prevTabId = prevTab.id;

      // reset all tabs
      this.resetTabs();

      // if previous tab exists, focus it. Else, focus the last tab in the list.
      if (prevTab !== 'undefined') {
        prevTab.focus();
        this.setActiveTab(prevTab, prevTabId);
      } else {
        const lastTab = this.tabNav.lastElementChild.firstElementChild;
        const lastTabId = lastTab.id;

        lastTab.focus();
        this.setActiveTab(lastTab, lastTabId);
      }
    }

    // on Tab-key, jump to active tabpanel (prevent keyboard-trap on firefox)
    if (keyCode === 9) {
      this.tabPanel.forEach(panel => {
        const activePanel = panel.getAttribute('aria-hidden');

        if (activePanel === 'false') {
          panel.firstElementChild.focus();
        }
      });
    }
  };

  destroy = () => {
    this.tabButton.forEach(tabBtn => {
      removeListener(
        tabBtn,
        'keypress click',
        this.toggleTabs.bind(this, tabBtn)
      );
      tabBtn.removeEventListener('keydown', this.setAccessibility);
    });

    this.tabButton.forEach(button =>
      setAttributes(button, {
        'aria-selected': 'false',
        tabindex: '0',
      })
    );

    this.tabPanel.forEach(panel =>
      setAttributes(panel, {
        'aria-hidden': 'false',
      })
    );
  };

  bindEvents = () => {
    this.tabButton.forEach(tabBtn => {
      addListener(tabBtn, 'keypress click', this.toggleTabs.bind(this, tabBtn));
      tabBtn.addEventListener('keydown', this.setAccessibility);
    });
  };

  init = () => {
    this.showRandom && this.getRandomItem();
    this.showFirst && this.initTabs(0);
    this.bindEvents();
  };
}
